import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  InputBase,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import StatusBadge from '../StatusBadge';
import InvoiceStatusBadge from '../../invoices/InvoiceStatusBadge';
import { formatPrice, formatPriceWithCurrency } from '../../../utils/vat';
import { availableActions, filterActions } from './actions';
import { searchGlobal } from './api';
import { isPaletteShortcut, PALETTE_TESTID } from './useCommandPalette';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const PER_GROUP_LIMIT = 5;

const GROUP_ICONS = {
  work_order: DirectionsCarIcon,
  customer: PersonIcon,
  vehicle: TimeToLeaveIcon,
  invoice: ReceiptIcon,
  part: Inventory2Icon,
  purchase_invoice: ReceiptLongIcon,
};

function formatQuantity(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return Number.isInteger(number) ? String(number) : formatPrice(number, 2);
}

/** Right-hand side of a result row, rendered from the free-form `meta` per type. */
function ResultMeta({ item, showInclVat, t }) {
  const meta = item.meta || {};
  switch (item.type) {
    case 'work_order':
      return (
        <StatusBadge
          status={meta.state || 'planned'}
          size="small"
          detail={meta.waiting_reason ? t(`waiting_reason.${meta.waiting_reason}`) : undefined}
        />
      );
    case 'invoice':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {meta.total != null && (
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {formatPriceWithCurrency(meta.total)}
            </Typography>
          )}
          <InvoiceStatusBadge
            status={meta.is_overdue ? 'overdue' : meta.status}
            invoice_type={meta.invoice_type}
            settlement_status={meta.settlement_status}
          />
        </Box>
      );
    case 'part': {
      const price = showInclVat ? meta.sell_price_incl_vat : meta.sell_price;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {price != null && (
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{formatPriceWithCurrency(price)}</Typography>
          )}
          {meta.stock != null && (
            <Chip size="small" variant="outlined" label={`${t('commandPalette.stock')} ${formatQuantity(meta.stock)}`} />
          )}
        </Box>
      );
    }
    case 'purchase_invoice':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {meta.total_gross != null && (
            <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {formatPriceWithCurrency(meta.total_gross)}
            </Typography>
          )}
          {meta.status && (
            <Chip
              size="small"
              variant="outlined"
              color={meta.is_overdue ? 'error' : 'default'}
              label={t(`purchaseLedger.status.${meta.status}`, { defaultValue: meta.status })}
            />
          )}
        </Box>
      );
    case 'vehicle':
      return meta.owner ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {meta.owner}
        </Typography>
      ) : null;
    default:
      return null;
  }
}

function RowIcon({ item }) {
  if (item.type === 'customer') {
    const Icon = item.meta?.kind === 'company' ? BusinessIcon : PersonIcon;
    return <Icon fontSize="small" />;
  }
  const Icon = GROUP_ICONS[item.type] || SearchIcon;
  return <Icon fontSize="small" />;
}

const subheaderSx = {
  lineHeight: '28px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'text.secondary',
  bgcolor: 'background.paper',
};

/**
 * Global search + actions (Ctrl+K). Input on top, grouped results, then the actions list.
 * Keyboard: ↑/↓ move one highlight across results and actions, Enter activates, Esc closes.
 * Focus never leaves the input.
 */
export default function CommandPalette({ open, onClose, onNavigate, onRunAction, initialQuery = '' }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;

  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const requestId = useRef(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const trimmed = query.trim();
  const searching = trimmed.length >= MIN_QUERY_LENGTH;

  // Open with the text handed over by a trigger (e.g. the dashboard search widget).
  useEffect(() => {
    if (open && initialQuery) setQuery(initialQuery);
  }, [open, initialQuery]);

  // Reset everything on close so the next open starts clean.
  useEffect(() => {
    if (open) return;
    setQuery('');
    setGroups([]);
    setLoading(false);
    setFailed(false);
    setHighlight(0);
    requestId.current += 1;
  }, [open]);

  // Debounced search; a stale response is ignored by request id and aborted where possible.
  useEffect(() => {
    if (!open) return undefined;
    if (!searching) {
      setGroups([]);
      setLoading(false);
      setFailed(false);
      return undefined;
    }
    const id = ++requestId.current;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchGlobal(trimmed, PER_GROUP_LIMIT, controller.signal);
        if (id !== requestId.current) return;
        setGroups(Array.isArray(data?.groups) ? data.groups : []);
        setFailed(false);
      } catch (error) {
        if (axios.isCancel(error) || id !== requestId.current) return;
        setGroups([]);
        setFailed(true);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, searching, trimmed]);

  const actions = useMemo(() => filterActions(availableActions(currentOrganization), trimmed, t), [currentOrganization, trimmed, t]);

  // One flat list for keyboard navigation: every result row, then every action.
  const rows = useMemo(() => {
    const results = groups.flatMap((group) => group.items.map((item) => ({ kind: 'result', type: group.type, ...item })));
    const actionRows = actions.map((action) => ({ kind: 'action', ...action }));
    return [...results, ...actionRows];
  }, [groups, actions]);

  useEffect(() => {
    setHighlight(0);
  }, [rows.length, trimmed]);

  useEffect(() => {
    const element = listRef.current?.querySelector(`[data-palette-index="${highlight}"]`);
    element?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const activate = useCallback(
    (row) => {
      if (!row) return;
      if (row.kind === 'action') {
        onRunAction(row);
      } else {
        onNavigate(row.url);
      }
    },
    [onNavigate, onRunAction]
  );

  const handleInputKeyDown = (event) => {
    if (isPaletteShortcut(event)) {
      event.preventDefault();
      inputRef.current?.select();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (rows.length) setHighlight((current) => (current + 1) % rows.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (rows.length) setHighlight((current) => (current - 1 + rows.length) % rows.length);
    } else if (event.key === 'Home' && rows.length) {
      event.preventDefault();
      setHighlight(0);
    } else if (event.key === 'End' && rows.length) {
      event.preventDefault();
      setHighlight(rows.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      activate(rows[highlight]);
    }
  };

  // Esc closes here and must not reach page-level shortcuts (Kassamyynti clears its cart on Esc).
  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const showNoResults = searching && !loading && !failed && groups.length === 0;
  let index = -1;

  const renderRow = (row, Icon, primary, secondary, right, testId) => {
    index += 1;
    const rowIndex = index;
    return (
      <ListItemButton
        key={testId}
        selected={rowIndex === highlight}
        data-palette-index={rowIndex}
        data-testid={testId}
        onMouseEnter={() => setHighlight(rowIndex)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => activate(row)}
        sx={{ py: 0.75, gap: 1, '&.Mui-selected': { bgcolor: 'action.selected' } }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{Icon}</ListItemIcon>
        <ListItemText
          primary={primary}
          secondary={secondary || undefined}
          primaryTypographyProps={{ noWrap: true, fontSize: 14, fontWeight: 600 }}
          secondaryTypographyProps={{ noWrap: true, fontSize: 12.5 }}
          sx={{ my: 0, minWidth: 0 }}
        />
        {right ? <Box sx={{ flexShrink: 0, ml: 1 }}>{right}</Box> : null}
      </ListItemButton>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      data-testid={PALETTE_TESTID}
      onKeyDown={handleDialogKeyDown}
      // autoFocus puts the caret in the input on mount; this repeats it after the open
      // transition because MUI's FocusTrap, double-invoked by StrictMode in development,
      // hands focus back to the previously focused field and then to the dialog container.
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        sx: {
          mt: fullScreen ? 0 : '8vh',
          borderRadius: fullScreen ? 0 : '16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <SearchIcon sx={{ color: 'text.secondary' }} />
        <InputBase
          autoFocus
          fullWidth
          inputRef={inputRef}
          value={query}
          placeholder={t('commandPalette.placeholder')}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleInputKeyDown}
          inputProps={{
            'data-testid': 'global-search-input',
            'aria-label': t('commandPalette.openSearch'),
            autoComplete: 'off',
            autoCorrect: 'off',
            autoCapitalize: 'off',
            spellCheck: 'false',
          }}
          sx={{ fontSize: 16, py: 0.5 }}
        />
        {fullScreen ? (
          <IconButton edge="end" onClick={onClose} aria-label={t('commandPalette.close')}>
            <CloseIcon />
          </IconButton>
        ) : (
          <Box
            component="kbd"
            sx={{ fontFamily: 'inherit', fontSize: 11, px: 0.75, py: 0.5, borderRadius: '4px', border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}
          >
            Esc
          </Box>
        )}
      </Box>
      <LinearProgress sx={{ height: 2, visibility: loading ? 'visible' : 'hidden' }} />

      <Box ref={listRef} sx={{ overflowY: 'auto', maxHeight: fullScreen ? 'none' : '60vh', flex: fullScreen ? 1 : 'none' }}>
        {groups.map((group) => (
          <List
            key={group.type}
            dense
            disablePadding
            subheader={<ListSubheader disableSticky sx={subheaderSx}>{t(`commandPalette.groups.${group.type}`)}</ListSubheader>}
            data-testid={`global-search-group-${group.type}`}
          >
            {group.items.map((item) => {
              const row = { kind: 'result', type: group.type, ...item };
              return renderRow(
                row,
                <RowIcon item={row} />,
                item.title,
                item.subtitle,
                <ResultMeta item={row} showInclVat={showInclVat} t={t} />,
                `global-search-result-${group.type}-${item.id}`
              );
            })}
          </List>
        ))}

        {failed && (
          <Typography sx={{ px: 2, py: 2, fontSize: 14, color: 'error.main' }}>{t('commandPalette.error')}</Typography>
        )}
        {showNoResults && (
          <Typography sx={{ px: 2, py: 2, fontSize: 14, color: 'text.secondary' }} data-testid="global-search-no-results">
            {t('commandPalette.noResults')}
          </Typography>
        )}

        {actions.length > 0 && (
          <List
            dense
            disablePadding
            subheader={<ListSubheader disableSticky sx={subheaderSx}>{t('commandPalette.actionsHeader')}</ListSubheader>}
            data-testid="global-search-actions"
          >
            {actions.map((action) => {
              const row = { kind: 'action', ...action };
              const Icon = action.icon;
              return renderRow(row, <Icon fontSize="small" />, t(action.labelKey), null, null, `global-search-action-${action.key}`);
            })}
          </List>
        )}
      </Box>
    </Dialog>
  );
}
